import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Plus, Video } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import meetingsService from '@/services/meetings.service';

interface MeetingsCardProps {
  /** Schmale Seitenleiste — nur das Symbol. */
  collapsed?: boolean;
}

/**
 * Meetings-Karte in der Seitenleiste — über der Telefonkarte, gleicher Look.
 *
 * Der Popover ist der Schnellzugriff: Meeting anlegen und aktiven beitreten,
 * ohne erst die Seite zu öffnen. Der eigentliche Gesprächsraum lebt auf
 * /meetings — beitreten navigiert dorthin und joint automatisch.
 */
export default function MeetingsCard({ collapsed = false }: MeetingsCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);

  const { data: meetings } = useQuery({
    queryKey: ['meetings'],
    queryFn: () => meetingsService.list(),
    refetchInterval: 20_000,
    retry: false,
  });
  const count = meetings?.length ?? 0;

  const goJoin = (id: string) => {
    setOpen(false);
    navigate('/meetings', { state: { joinId: id } });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-live="false"
          className={cn(
            'vyndesk-call-status flex items-center gap-2 rounded-md border text-xs font-medium transition',
            collapsed ? 'h-8 w-8 justify-center px-0' : 'h-9 w-full px-2.5',
          )}
          title={t('shell:meet.title', 'Meetings')}
        >
          <Video className="h-3.5 w-3.5 shrink-0" />
          {!collapsed && (
            <>
              <span className="min-w-0 flex-1 truncate text-left">{t('shell:meet.title', 'Meetings')}</span>
              {count > 0 && (
                <span className="shrink-0 rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-violet-700">
                  {count}
                </span>
              )}
            </>
          )}
          {collapsed && count > 0 && (
            <span className="absolute -mt-4 ml-4 rounded bg-violet-500 px-1 text-[9px] font-bold text-white">
              {count}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent side="right" align="end" className="w-64 rounded-md p-3">
        {/* Anlegen fragt zuerst (Org + Kollegen) — Dialog lebt auf /meetings */}
        <Button
          size="sm"
          className="h-9 w-full rounded-md"
          onClick={() => { setOpen(false); navigate('/meetings', { state: { create: true } }); }}
        >
          <Plus className="mr-1.5 h-4 w-4" />
          {t('shell:meet.new', 'Neues Meeting')}
        </Button>

        {/* Aktive Meetings — klick = beitreten */}
        {count > 0 && (
          <ul className="mt-2.5 space-y-1">
            {[...(meetings || [])].sort((a, b) => Number(b.is_invited) - Number(a.is_invited)).slice(0, 4).map((m) => {
              const host = [m.first_name, m.last_name].filter(Boolean).join(' ');
              return (
                <li key={m.id}>
                  <button
                    type="button"
                    onClick={() => goJoin(m.id)}
                    className="flex w-full items-center gap-2 rounded-md border border-border px-2 py-1.5 text-left transition hover:bg-accent"
                  >
                    <Video className="h-3.5 w-3.5 shrink-0 text-violet-500" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-xs font-medium text-foreground">
                        {m.title || t('shell:meet.defaultTitle', 'Video-Gespräch')}
                      </span>
                      {host && <span className="block truncate text-[10px] text-muted-foreground">{host}</span>}
                    </span>
                    <span className="shrink-0 text-[10px] font-medium text-violet-600">
                      {m.is_invited
                        ? t('shell:meet.invitedYou', 'Du bist eingeladen')
                        : t('shell:meet.join', 'Beitreten')}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <button
          type="button"
          onClick={() => { setOpen(false); navigate('/meetings'); }}
          className="mt-2 w-full text-center text-[11px] font-medium text-muted-foreground hover:text-foreground"
        >
          {t('shell:meet.showAll', 'Alle Meetings anzeigen')}
        </button>
      </PopoverContent>
    </Popover>
  );
}
